import { format } from "date-fns";
import { html, nothing } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { translate } from "../../localization";
import { hasItemDivider, shouldShowRole } from "../domain/business-rules";
import {
  IActivityHistoryDiagnostic,
  IActivityHistoryGroup,
  IActivityHistoryItem,
  IActivityHistoryItemWithComponent,
} from "../domain/model";
import {
  ActivityHistoryEntry,
  mergeHistoryItemsAndSubgroups,
} from "../list/merge-history-items-and-subgroups/merge-history-items-and-subgroups";
import { ActivityHistoryImporter } from "./activity-history-importer";

const checkboxState = (state: "all" | "some" | "none") => ({
  checked: state === "all",
  indeterminate: state === "some",
});

export const template = (props: ActivityHistoryImporter) => {
  const all = checkboxState(props.allState());

  return html`
    <div class="importer">
      ${headerTemplate(props)} ${searchTemplate(props)} ${filtersTemplate(props, all)} ${listTemplate(props)}
    </div>
  `;
};

const headerTemplate = (props: ActivityHistoryImporter) => html`
  <div class="importer__header">
    <dss-typography tag="h4" variant="subtitle-3">${translate("importer.title")}</dss-typography>
    <dss-badge text="${props.totalCount}" state="info" size="sm" outlined hideIcon></dss-badge>
  </div>
`;

const searchTemplate = (props: ActivityHistoryImporter) => html`
  <div class="importer__search">
    <activity-history-search-bar
      .searchString=${props.searchString}
      @onSearchChange=${(e: CustomEvent) => props._onSearchChange(e)}
    ></activity-history-search-bar>
  </div>
`;

const filtersTemplate = (props: ActivityHistoryImporter, all: { checked: boolean; indeterminate: boolean }) => html`
  <div class="importer__filters">
    <dss-form-checkbox
      label=${translate("importer.selectAll")}
      .checked=${all.checked}
      .indeterminate=${all.indeterminate}
      @change=${(e: Event) => props._toggleAll(e)}
    ></dss-form-checkbox>

    <span class="importer__filters-label">${translate("importer.filters")}</span>

    <dss-form-checkbox
      label=${translate("importer.today")}
      .checked=${props.todayOnly}
      @change=${(e: Event) => props._onToggleToday(e)}
    ></dss-form-checkbox>

    ${
      props.hasDiagnosticsFilter()
        ? html`
            <dss-form-select
              dropdownStyle="max-height: 300px; overflow-y: auto;"
              size="sm"
              type="default"
              multiple
              dropdownFixed
              placeholder="${translate("importer.diagnostics")}"
              .elements=${props.diagnosticsOptions()}
              @value-changed=${(e: CustomEvent) => props._onDiagnosticsChanged(e)}
            ></dss-form-select>
          `
        : nothing
    }
  </div>
`;

const listTemplate = (props: ActivityHistoryImporter) => {
  if (!props.filteredGroups?.length) {
    return html`<div class="importer__empty">${translate("importer.noResults")}</div>`;
  }

  return html`
    <div class="virtual-container">
      <div class="virtual-viewport">
        <div class="virtual-content" style="height: ${props._totalHeight}px;">
          <div class="virtual-items" style="transform: translateY(${props._containerOffset}px)">
            ${repeat(
              props._visibleRows,
              (row) => row.group.idGroup,
              (row) => visitTemplate(props, row.group, row.index),
            )}
          </div>
        </div>
      </div>
    </div>
  `;
};

const visitTemplate = (props: ActivityHistoryImporter, group: IActivityHistoryGroup, index: number) => {
  const representative = group.items?.[0] || group.subGroups?.[0]?.items?.[0];
  const visit = checkboxState(props.visitState(group));

  return html`
    <div class="visit" data-index=${index} ${ref(props._measureElement)} data-date=${ifDefined(representative?.date)}>
      <div class="visit__header">
        <dss-form-checkbox
          .checked=${visit.checked}
          .indeterminate=${visit.indeterminate}
          hideLabel
          @change=${(e: Event) => props._toggleVisit(group, (e.target as HTMLInputElement).checked)}
        ></dss-form-checkbox>
        ${visitHeaderContent(representative)}
      </div>
      <div class="visit__items">
        ${repeat(
          mergeHistoryItemsAndSubgroups(group),
          (entry) => (entry.type === "item" ? entry.item.id : (entry.subGroup as any).idSubGroup),
          (entry: ActivityHistoryEntry) => {
            if (entry.type === "item") {
              return itemRow(props, entry.item, group.items as IActivityHistoryItemWithComponent[]);
            }
            return subGroupTemplate(props, entry.subGroup);
          },
        )}
      </div>
    </div>
  `;
};

const visitHeaderContent = (item: IActivityHistoryItem | undefined) => {
  if (!item) return nothing;
  const showRole = shouldShowRole(item);
  const values: string[] = [];
  if (item.professional?.name) values.push(item.professional.name);
  if (showRole && item.professional?.role?.description) values.push(item.professional.role.description);
  if (item.professional?.speciality?.description) values.push(item.professional.speciality.description);
  if (item.up?.description) values.push(item.up.description);
  if (item.service?.description) values.push(item.service.description);

  return html`
    <div class="title">
      <div class="title-date">
        <div>${format(new Date(item.date), "dd/MM/yyyy")}</div>
      </div>
      <div class="title-description">
        <div>${values.map((v, i) => html`${v}${i < values.length - 1 ? " | " : ""}`)}</div>
      </div>
    </div>
  `;
};

const subGroupTemplate = (props: ActivityHistoryImporter, subGroup: { items: IActivityHistoryItemWithComponent[] }) => {
  const sub = checkboxState(props.subGroupState(subGroup.items));

  return html`
    <div class="diagnostics">
      <div class="diagnostics__header">
        <dss-form-checkbox
          .checked=${sub.checked}
          .indeterminate=${sub.indeterminate}
          hideLabel
          @change=${(e: Event) => props._toggleSubGroup(subGroup.items, (e.target as HTMLInputElement).checked)}
        ></dss-form-checkbox>
        ${diagnosticHeaderTemplate(subGroup.items?.[0])}
      </div>
      <div class="diagnostics__items">
        ${repeat(
          subGroup.items,
          (item) => item.id,
          (item) => itemRow(props, item, subGroup.items),
        )}
      </div>
    </div>
  `;
};

const itemRow = (
  props: ActivityHistoryImporter,
  item: IActivityHistoryItemWithComponent,
  siblings: IActivityHistoryItemWithComponent[],
) => html`
  <div class="item" ?has-divider=${hasItemDivider(item, siblings)}>
    <dss-form-checkbox
      class="item__check"
      .checked=${props.isItemSelected(item.id)}
      hideLabel
      @change=${() => props._toggleItem(item.id)}
    ></dss-form-checkbox>
    ${item.component}
  </div>
`;

const diagnosticHeaderTemplate = (item: IActivityHistoryItem | undefined) =>
  item &&
  html`
    ${item.diagnostics
      .filter((diagnostic: IActivityHistoryDiagnostic) => diagnostic.descripcio != null && diagnostic.descripcio.trim() !== "")
      .map(
        (diagnostic: IActivityHistoryDiagnostic) =>
          html`<dss-badge text=${diagnostic.descripcio?.toUpperCase() as string} state="info" size="sm" outlined hideIcon></dss-badge>`,
      )}
  `;
